import type { PatSnapSuccessResponseData } from '@patsnap/tahiti-core';
import { PsHttpService } from '@patsnap/tahiti-core';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import type {
    OarTaskApi,
    CreateTaskWithPdfRequestParams,
    OarTaskCreateResponseData,
    GetTaskInfoRequestParams,
    OarTaskInfoResponseData,
    ParseOfficeActionHeaderRequestParams,
    OfficeActionHeaderResponseData,
    GetOfficeActionHeaderRequestParams,
    ParseOfficeActionTableRequestParams,
    OfficeActionTableInfoResponseData,
    GetOfficeActionTableRequestParams,
    ParseOfficeActionBodyRequestParams,
    OfficeActionBodyContentResponseData,
    GetOfficeActionBodyRequestParams,
    AnalyzeClaimsRequestParams,
    ClaimAnalysisResponseData,
    SubmitClaimAnalysisTaskRequestParams,
    ClaimAnalysisTaskSubmitResponseData,
    GetClaimAnalysisTaskStatusRequestParams,
    ClaimAnalysisTaskStatusData,
    GetClaimAnalysisTaskResultRequestParams,
    CancelClaimAnalysisTaskRequestParams,
} from '../oar-task-api';
import type { ExportStatementApi, RequestParams, ResponseData } from '../export-statement-api';
import type { PreviewStatementPromptApi, PreviewStatementPromptRequestParams, PreviewStatementPromptResponseData } from '../preview-statement-prompt-api';
import type { GenerateStatementOpinionApi, GenerateStatementOpinionApiRequestParams, GenerateStatementOpinionApiResponseData } from '../generate-statement-opinion-api';
import type { CancelComparisonPageTaskParams, ComparisonPageTaskApi, ComparisonPageTaskResultResponseData, ComparisonPageTaskStatusParams, ComparisonPageTaskStatusResponseData, GenerateComparisonPageAsyncParams, GenerateComparisonPageAsyncResponseData } from '../comparison-page-task-api';
import type { CancelTaskRequestParams, CancelTaskResponseData, GetTaskResultRequestParams, GetTaskStatusRequestParams, StatementOpinionAsyncApi, SubmitTaskRequestParams, SubmitTaskResponseData, TaskResultResponseData, TaskStatusResponseData } from '../statement-opinion-async-api';
import type {
    BodyParseTaskApi,
    BodyParseTaskResultResponse,
    BodyParseTaskStatusResponse,
    BodyParseTaskSubmitResponse,
    CancelTaskRequestParams as BodyParseCancelTaskRequestParams,
    GetTaskResultRequestParams as BodyParseGetTaskResultRequestParams,
    GetTaskStatusRequestParams as BodyParseGetTaskStatusRequestParams,
    ParseBodyAsyncRequestParams,
} from '../body-parse-task-api';

/**
 * Implementation of OarTaskApi interface
 *
 * Provides concrete implementation for OAR (Office Action Response) task management,
 * including task creation, PDF upload, and parsing operations for header, table, and body content.
 */
@injectable()
export class OarTaskApiImpl extends PsHttpService implements
    OarTaskApi, ExportStatementApi,
    GenerateStatementOpinionApi,
    ComparisonPageTaskApi,
    StatementOpinionAsyncApi,
    BodyParseTaskApi,
    PreviewStatementPromptApi {
    /**
     * Submit async body parse task
     *
     * @param params - Request parameters including task_id
     * @returns Promise resolving to task submission response
     */
    async parseOfficeActionBodyAsync(params: ParseBodyAsyncRequestParams): Promise<BodyParseTaskSubmitResponse> {
        const result = await this.post<PatSnapSuccessResponseData<BodyParseTaskSubmitResponse>>({
            url: `/ai-oar/tasks/${params.path.task_id}/body/parse-async`,
            data: {},
        });
        return result.data;
    }

    /**
     * Get body parse task status
     *
     * @param params - Request parameters including async_task_id
     * @returns Promise resolving to task status information
     */
    async getBodyParseTaskStatus(params: BodyParseGetTaskStatusRequestParams): Promise<BodyParseTaskStatusResponse> {
        const result = await this.get<PatSnapSuccessResponseData<BodyParseTaskStatusResponse>>({
            url: `/ai-oar/body-parse-tasks/${params.path.async_task_id}/status`,
        });
        return result.data;
    }

    /**
     * Get body parse task result
     *
     * @param params - Request parameters including async_task_id
     * @returns Promise resolving to body parse result
     */
    async getBodyParseTaskResult(params: BodyParseGetTaskResultRequestParams): Promise<BodyParseTaskResultResponse> {
        const result = await this.get<PatSnapSuccessResponseData<BodyParseTaskResultResponse>>({
            url: `/ai-oar/body-parse-tasks/${params.path.async_task_id}/result`,
        });
        return result.data;
    }

    /**
     * Cancel body parse task
     *
     * @param params - Request parameters including async_task_id
     * @returns Promise resolving when task is cancelled
     */
    async cancelBodyParseTask(params: BodyParseCancelTaskRequestParams): Promise<void> {
        await this.delete<PatSnapSuccessResponseData<void>>({
            url: `/ai-oar/body-parse-tasks/${params.path.async_task_id}`,
        });
    }

    async generateStatementOpinionAsync(params: SubmitTaskRequestParams): Promise<SubmitTaskResponseData> {
        const result = await this.post<PatSnapSuccessResponseData<SubmitTaskResponseData>>({
            url: '/ai-oar/statements/generate-opinion-async',
            data: params.body,
        });
        return result.data;
    }

    async getStatementOpinionTaskStatus(params: GetTaskStatusRequestParams): Promise<TaskStatusResponseData> {
        const result = await this.get<PatSnapSuccessResponseData<TaskStatusResponseData>>({
            url: `/ai-oar/statement-opinion-tasks/${params.path.async_task_id}/status`,
        });
        return result.data;
    }

    async getStatementOpinionTaskResult(params: GetTaskResultRequestParams): Promise<TaskResultResponseData> {
        const result = await this.get<PatSnapSuccessResponseData<TaskResultResponseData>>({
            url: `/ai-oar/statement-opinion-tasks/${params.path.async_task_id}/result`,
        });
        return result.data;
    }

    async cancelStatementOpinionTask(params: CancelTaskRequestParams): Promise<CancelTaskResponseData> {
        await this.delete<PatSnapSuccessResponseData<CancelTaskResponseData>>({
            url: `/ai-oar/statement-opinion-tasks/${params.path.async_task_id}`,
        });
        return true;
    }

    get options() {
        return {
            baseURL: coreTelecomApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }

    async generateComparisonPageAsync(params: GenerateComparisonPageAsyncParams): Promise<GenerateComparisonPageAsyncResponseData> {
        const result = await this.post<PatSnapSuccessResponseData<GenerateComparisonPageAsyncResponseData>>({
            url: '/ai-oar/statements/comparison-page-async',
            data: params.body,
        });
        return result.data;
    }

    async getComparisonPageTaskStatus(params: ComparisonPageTaskStatusParams): Promise<ComparisonPageTaskStatusResponseData> {
        const result = await this.get<PatSnapSuccessResponseData<ComparisonPageTaskStatusResponseData>>({
            url: `/ai-oar/comparison-page-tasks/${params.path.async_task_id}/status`,
        });
        return result.data;
    }

    async getComparisonPageTaskResult(params: ComparisonPageTaskStatusParams): Promise<ComparisonPageTaskResultResponseData> {
        const result = await this.get<PatSnapSuccessResponseData<ComparisonPageTaskResultResponseData>>({
            url: `/ai-oar/comparison-page-tasks/${params.path.async_task_id}/result`,
        });
        return result.data;
    }

    async cancelComparisonPageTask(params: CancelComparisonPageTaskParams): Promise<void> {
        await this.delete<PatSnapSuccessResponseData<void>>({
            url: `/ai-oar/comparison-page-tasks/${params.path.async_task_id}`,
        });
    }

    async generateStatementOpinion(params: GenerateStatementOpinionApiRequestParams): Promise<GenerateStatementOpinionApiResponseData> {
        const result = await this.post<PatSnapSuccessResponseData<GenerateStatementOpinionApiResponseData>>({
            url: '/ai-oar/statements/generate-opinion',
            data: params.body,
        });
        return result.data;
    }

    async previewStatementPrompt(params: PreviewStatementPromptRequestParams): Promise<PreviewStatementPromptResponseData> {
        const result = await this.post<PatSnapSuccessResponseData<PreviewStatementPromptResponseData>>({
            url: '/ai-oar/statements/export',
            data: params.body,
        });
        return result.data;
    }

    async exportStatement(params: RequestParams): Promise<ResponseData> {
        const result = await this.post<PatSnapSuccessResponseData<ResponseData>>({
            url: '/ai-oar/statements/export',
            data: params.body,
        });
        return result.data;
    }

    /**
     * Create OAR task and upload PDF
     *
     * @param params - Request parameters including task_id and file
     * @returns Promise resolving to task creation response
     */
    async createTaskWithPdf(params: CreateTaskWithPdfRequestParams): Promise<OarTaskCreateResponseData> {
        const formData = new FormData();
        formData.append('file', params.upload.file);

        const result = await this.post<PatSnapSuccessResponseData<OarTaskCreateResponseData>>({
            url: `/ai-oar/tasks/${params.path.task_id}`,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return result.data;
    }

    /**
     * Get OAR task information
     *
     * @param params - Request parameters including task_id
     * @returns Promise resolving to task information
     */
    async getTaskInfo(params: GetTaskInfoRequestParams): Promise<OarTaskInfoResponseData> {
        const result = await this.get<PatSnapSuccessResponseData<OarTaskInfoResponseData>>({
            url: `/ai-oar/tasks/${params.path.task_id}`,
        });
        return result.data;
    }

    /**
     * Parse office action header
     *
     * @param params - Request parameters including task_id
     * @returns Promise resolving to parsed header information
     */
    async parseOfficeActionHeader(params: ParseOfficeActionHeaderRequestParams): Promise<OfficeActionHeaderResponseData> {
        const result = await this.post<PatSnapSuccessResponseData<OfficeActionHeaderResponseData>>({
            url: `/ai-oar/tasks/${params.path.task_id}/header/parse`,
            data: {},
        });
        return result.data;
    }

    /**
     * Get office action header
     *
     * @param params - Request parameters including task_id
     * @returns Promise resolving to header information
     */
    async getOfficeActionHeader(params: GetOfficeActionHeaderRequestParams): Promise<OfficeActionHeaderResponseData> {
        const result = await this.get<PatSnapSuccessResponseData<OfficeActionHeaderResponseData>>({
            url: `/ai-oar/tasks/${params.path.task_id}/header`,
        });
        return result.data;
    }

    /**
     * Parse office action table
     *
     * @param params - Request parameters including task_id
     * @returns Promise resolving to parsed table information
     */
    async parseOfficeActionTable(params: ParseOfficeActionTableRequestParams): Promise<OfficeActionTableInfoResponseData> {
        const result = await this.post<PatSnapSuccessResponseData<OfficeActionTableInfoResponseData>>({
            url: `/ai-oar/tasks/${params.path.task_id}/table/parse`,
            data: {},
        });
        return result.data;
    }

    /**
     * Get office action table
     *
     * @param params - Request parameters including task_id
     * @returns Promise resolving to table information
     */
    async getOfficeActionTable(params: GetOfficeActionTableRequestParams): Promise<OfficeActionTableInfoResponseData> {
        const result = await this.get<PatSnapSuccessResponseData<OfficeActionTableInfoResponseData>>({
            url: `/ai-oar/tasks/${params.path.task_id}/table`,
        });
        return result.data;
    }

    /**
     * Parse office action body
     *
     * @param params - Request parameters including task_id
     * @returns Promise resolving to parsed body content
     */
    async parseOfficeActionBody(params: ParseOfficeActionBodyRequestParams): Promise<OfficeActionBodyContentResponseData> {
        const result = await this.post<PatSnapSuccessResponseData<OfficeActionBodyContentResponseData>>({
            url: `/ai-oar/tasks/${params.path.task_id}/body/parse`,
            data: {},
        });
        return result.data;
    }

    /**
     * Get office action body
     *
     * @param params - Request parameters including task_id
     * @returns Promise resolving to body content
     */
    async getOfficeActionBody(params: GetOfficeActionBodyRequestParams): Promise<OfficeActionBodyContentResponseData> {
        const result = await this.get<PatSnapSuccessResponseData<OfficeActionBodyContentResponseData>>({
            url: `/ai-oar/tasks/${params.path.task_id}/body`,
        });
        return result.data;
    }

    /**
     * Analyze claims for a specific conclusive opinion
     *
     * @param params - Request parameters including task_id and conclusive_opinion_id
     * @returns Promise resolving to claim analysis results
     */
    async analyzeClaims(params: AnalyzeClaimsRequestParams): Promise<ClaimAnalysisResponseData> {
        const result = await this.post<PatSnapSuccessResponseData<ClaimAnalysisResponseData>>({
            url: `/ai-oar/tasks/${params.path.task_id}/conclusive-opinions/${params.path.conclusive_opinion_id}/claims/analyze`,
            data: {},
        });
        return result.data;
    }

    /**
     * Submit async claim analysis task
     *
     * @param params - Request parameters including task_id and conclusive_opinion_id
     * @returns Promise resolving to task submission response
     */
    async submitClaimAnalysisTask(params: SubmitClaimAnalysisTaskRequestParams): Promise<ClaimAnalysisTaskSubmitResponseData> {
        const result = await this.post<PatSnapSuccessResponseData<ClaimAnalysisTaskSubmitResponseData>>({
            url: `/ai-oar/tasks/${params.path.task_id}/conclusive-opinions/${params.path.conclusive_opinion_id}/claims/analyze-async`,
            data: {},
        });
        return result.data;
    }

    /**
     * Get claim analysis task status
     *
     * @param params - Request parameters including async_task_id
     * @returns Promise resolving to task status information
     */
    async getClaimAnalysisTaskStatus(params: GetClaimAnalysisTaskStatusRequestParams): Promise<ClaimAnalysisTaskStatusData> {
        const result = await this.get<PatSnapSuccessResponseData<ClaimAnalysisTaskStatusData>>({
            url: `/ai-oar/claim-analysis-tasks/${params.path.async_task_id}/status`,
        });
        return result.data;
    }

    /**
     * Get claim analysis task result
     *
     * @param params - Request parameters including async_task_id
     * @returns Promise resolving to claim analysis results
     */
    async getClaimAnalysisTaskResult(params: GetClaimAnalysisTaskResultRequestParams): Promise<ClaimAnalysisResponseData> {
        const result = await this.get<PatSnapSuccessResponseData<ClaimAnalysisResponseData>>({
            url: `/ai-oar/claim-analysis-tasks/${params.path.async_task_id}/result`,
        });
        return result.data;
    }

    /**
     * Cancel claim analysis task
     *
     * @param params - Request parameters including async_task_id
     * @returns Promise resolving when task is cancelled
     */
    async cancelClaimAnalysisTask(params: CancelClaimAnalysisTaskRequestParams): Promise<void> {
        await this.delete({
            url: `/ai-oar/claim-analysis-tasks/${params.path.async_task_id}`,
        });
    }
}
